/**
 * yuna-sl-unit.js — per-variant live reflector for the "unit-by studiolab" block.
 *
 * Yuna keeps units-per-pack on the VARIANT (`custom.unidade`), so switching pack
 * size changes BOTH the price and the divisor. The library's Kaching reflector
 * only rescales the price against a static units-per-pack, which would show a
 * wrong figure here — this file replaces it.
 *
 * It reads nothing off the screen: no price-text scraping, no polling, no
 * MutationObserver. It listens to Shopify's standard `shopify:product:select`
 * event, which Horizon's variant picker dispatches on every selection, and looks
 * both values up in the per-variant map that blocks/sl-unit.liquid emits into
 * `window.slUnitData`. Currency and locale come from the badge's own data-*
 * attributes — nothing hardcoded.
 *
 * Horizon 3 announced the same change as `variant:update`. Horizon 4.1.5 no
 * longer dispatches that event, and a listener waiting for it raises no error:
 * the badge simply kept the first pack's figure.
 *
 * The event bubbles to document, so one delegated listener covers the product
 * page, product cards, and quick-add alike. Badges are matched by product id, so
 * a collection page full of cards only ever updates the card that changed.
 */
(function () {
  'use strict';

  // The block emits one <script defer> per instance; a page with several badges
  // would otherwise register the listener several times.
  if (window.__yunaSlUnitInit) return;
  window.__yunaSlUnitInit = true;

  var SEL = '[data-sl-unit]';
  var PENDING_CLASS = 'sl-unit--pending';

  function formatMoney(major, locale, currency) {
    try {
      return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currency }).format(major);
    } catch (e) {
      return major.toFixed(2);
    }
  }

  function apply(el, units, priceMinor) {
    if (!(units > 0) || !(priceMinor > 0)) {
      el.classList.add(PENDING_CLASS);
      return;
    }
    // Math.floor mirrors Liquid's integer `divided_by` in the block, so the live
    // value can never disagree with the server-rendered one by a cent.
    var perUnitMinor = Math.floor(priceMinor / units);
    var text = formatMoney(perUnitMinor / 100, el.dataset.locale, el.dataset.currency);
    var suffix = el.dataset.suffix || '';
    if (suffix) text += ' ' + suffix;

    if (el.textContent !== text) el.textContent = text;
    el.dataset.unitsPerPack = String(units);
    el.classList.remove(PENDING_CLASS);
  }

  function update(productId, variantId) {
    if (!variantId || !productId) return;

    var byVariant = window.slUnitData && window.slUnitData[productId];
    if (!byVariant) return;

    var entry = byVariant[variantId];
    if (!entry) return;

    var badges = document.querySelectorAll(SEL);
    for (var i = 0; i < badges.length; i++) {
      var el = badges[i];
      // Combined listings and collection pages put many products on one page;
      // only touch badges belonging to the product that actually changed.
      if (String(el.dataset.productId) !== String(productId)) continue;
      apply(el, entry.units, entry.price);
    }
  }

  document.addEventListener('shopify:product:select', function (event) {
    // The event starts at the variant picker, which carries the product id.
    var target = event.target;
    var owner = target && target.closest ? target.closest('[data-product-id]') : null;
    var productId = owner && owner.getAttribute('data-product-id');

    // The selected variant is on the event immediately, so the badge changes on
    // click instead of after the section fetch.
    var variantId = event.detail && event.detail.variantId;
    if (variantId) {
      update(productId, variantId);
      return;
    }

    // Selections that only resolve to a variant once that fetch returns.
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(function (result) {
        var detail = (result && result.detail) || {};
        update(detail.productId || productId, detail.resource && detail.resource.id);
      }, function () {});
    }
  });
})();
